export const quizQuestions = [
  // ===== SECTION 1: Business Health Assessment (10 Yes/No Questions) =====
  {
    id: 'q1',
    type: 'yesno',
    category: 'operations',
    question: 'Do you have documented processes for your core operations?',
    subtitle: 'Simple explanation: Do you have written instructions that show anyone how to do the main tasks in your business?',
    definition: 'Documented processes = Step-by-step guides for how work gets done (like a recipe book for your business)',
    weight: 1
  },
  {
    id: 'q2',
    type: 'yesno',
    category: 'operations',
    question: 'Can your business run for a week without you being present?',
    subtitle: 'Simple explanation: If you went on vacation for 7 days, would your business still operate normally?',
    definition: 'This measures if your team can handle things without you calling or checking in constantly',
    weight: 1.5
  },
  {
    id: 'q3',
    type: 'yesno',
    category: 'financial',
    question: 'Do you have consistent monthly revenue (within ±20% variance)?',
    subtitle: 'Simple explanation: Does your business make roughly the same amount of money each month?',
    definition: 'Consistent revenue = Your monthly sales don\'t swing wildly up and down (within 20% is stable)',
    weight: 1.5
  },
  {
    id: 'q4',
    type: 'yesno',
    category: 'financial',
    question: 'Is your customer acquisition cost (CAC) clearly tracked?',
    subtitle: 'Simple explanation: Do you know exactly how much money you spend to get one new customer?',
    definition: 'CAC = Total marketing/sales costs ÷ Number of new customers (e.g., spend $1,000, get 10 customers = $100 CAC)',
    weight: 1
  },
  {
    id: 'q5',
    type: 'yesno',
    category: 'operations',
    question: 'Do you have a reliable team or key managers in place?',
    subtitle: 'Simple explanation: Do you have people you trust to run the business when you\'re not there?',
    definition: 'Key managers = People who can make decisions, solve problems, and keep things running without you',
    weight: 1.5
  },
  {
    id: 'q6',
    type: 'yesno',
    category: 'financial',
    question: 'Are your books up-to-date and audit-ready?',
    subtitle: 'Simple explanation: Are your financial records organized and current (could a banker or accountant review them today)?',
    definition: 'Books = Your business financial records (income, expenses, profits). Audit-ready = Clean and accurate',
    weight: 1
  },
  {
    id: 'q7',
    type: 'yesno',
    category: 'financial',
    question: 'Do you know your exact profit margins by product or service?',
    subtitle: 'Simple explanation: For each thing you sell, do you know how much profit you actually make?',
    definition: 'Profit margin = What you keep after paying all costs (e.g., sell for $100, costs $60 to make = $40 profit = 40% margin)',
    weight: 1
  },
  {
    id: 'q8',
    type: 'yesno',
    category: 'operations',
    question: 'Can you access real-time data on business performance?',
    subtitle: 'Simple explanation: Can you check right now how your business is doing (sales, customers, cash)?',
    definition: 'Real-time data = Live information you can see instantly (like a dashboard showing today\'s sales)',
    weight: 1
  },
  {
    id: 'q9',
    type: 'yesno',
    category: 'operations',
    question: 'Do you have a marketing system that generates leads consistently?',
    subtitle: 'Simple explanation: Do you have a way to get new potential customers every week or month automatically?',
    definition: 'Lead generation system = A repeatable method to find people interested in buying from you (not just luck)',
    weight: 1.5
  },
  {
    id: 'q10',
    type: 'yesno',
    category: 'owner',
    question: 'Are you working fewer than 60 hours per week in the business?',
    subtitle: 'Simple explanation: Do you work less than 12 hours a day Monday through Friday in your business?',
    definition: 'Working hours = Time spent doing business tasks (if you work 10+ hour days every day, that\'s 60+ hours/week)',
    weight: 1
  },

  // ===== SECTION 2: Qualification Questions (5 Multiple Choice) =====
  {
    id: 'q11',
    type: 'multiple',
    category: 'qualification',
    question: 'Which situation best describes your current situation?',
    options: [
      {
        value: 'burnout',
        label: 'Burned Out & Ready to Exit',
        description: 'You\'re exhausted and considering selling or stepping back entirely'
      },
      {
        value: 'stuck',
        label: 'Stuck with Broken Systems',
        description: 'Good product/service, but operations are a mess and limiting growth'
      },
      {
        value: 'life_change',
        label: 'Life Change Required',
        description: 'Health, family, or relocation forcing a business transition'
      },
      {
        value: 'growth',
        label: 'Ready to Scale',
        description: 'Business is working, but you need capital/expertise to grow faster'
      },
      {
        value: 'exploring',
        label: 'Just Exploring Options',
        description: 'Not urgent, but curious about what\'s possible'
      }
    ]
  },
  {
    id: 'q12',
    type: 'multiple',
    category: 'qualification',
    question: 'What is your desired outcome in the next 90 days?',
    options: [
      {
        value: 'exit_clean',
        label: 'Clean Exit / Full Sale',
        description: 'Cash out and move on to the next chapter'
      },
      {
        value: 'partner_majority',
        label: 'Partner with Majority Sale',
        description: 'Sell majority stake but stay involved in reduced capacity'
      },
      {
        value: 'partner_minority',
        label: 'Bring in Equity Partner',
        description: 'Get help fixing/scaling while maintaining ownership'
      },
      {
        value: 'prep_exit',
        label: 'Prepare for Future Exit',
        description: 'Get the business ready to sell at a higher valuation later'
      },
      {
        value: 'learn',
        label: 'Learn & Assess',
        description: 'Understand my options before making decisions'
      }
    ]
  },
  {
    id: 'q13',
    type: 'multiple',
    category: 'qualification',
    question: 'What is the biggest obstacle getting in the way of your goal?',
    options: [
      {
        value: 'time',
        label: 'No Time',
        description: 'Trapped in daily operations, can\'t work ON the business'
      },
      {
        value: 'money',
        label: 'Need Capital',
        description: 'Growth requires investment I don\'t have access to'
      },
      {
        value: 'systems',
        label: 'Broken Systems',
        description: 'Nothing is documented, everything depends on me'
      },
      {
        value: 'team',
        label: 'Unreliable Team',
        description: 'Can\'t find or keep good people'
      },
      {
        value: 'knowledge',
        label: 'Don\'t Know How',
        description: 'Need expertise in operations, marketing, or finance'
      },
      {
        value: 'tried_failed',
        label: 'Tried Things That Failed',
        description: 'Attempted improvements but nothing stuck'
      }
    ]
  },
  {
    id: 'q14',
    type: 'multiple',
    category: 'qualification',
    question: 'What is your annual revenue range?',
    options: [
      { value: 'under_50k', label: 'Under $50K', description: 'Early stage or side business' },
      { value: '50k_250k', label: '$50K - $250K', description: 'Growing small business' },
      { value: '250k_500k', label: '$250K - $500K', description: 'Established small business' },
      { value: '500k_1m', label: '$500K - $1M', description: 'Scaling business' },
      { value: '1m_2m', label: '$1M - $2M', description: 'Mature small business' },
      { value: 'over_2m', label: 'Over $2M', description: 'Larger operation' }
    ]
  },
  {
    id: 'q15',
    type: 'text',
    category: 'qualification',
    question: 'Is there anything else we should know about your situation?',
    subtitle: 'Share any context that would help us understand how we can best support you.'
  }
];
